import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { trackPhoneClick, trackWhatsAppClick } from "../utils/analytics";

const sections = [
  {
    title: "Booking Confirmation",
    items: [
      "A booking is confirmed only after Limassol Airport Taxi confirms the transfer details by phone, WhatsApp, email, or another direct communication channel.",
      "Please check your pickup location, destination, date, time, passenger count, luggage details, and contact details when your booking is confirmed.",
    ],
  },
  {
    title: "Fixed Prices and Fare Confirmation",
    items: [
      "Quoted fares are fixed for the confirmed route, pickup time, and destination unless the journey details change.",
      "Any extra stops, route changes, waiting time, or additional requirements should be agreed before or during the trip and may affect the final fare.",
    ],
  },
  {
    title: "Flight Monitoring",
    items: [
      "When you provide a valid flight number, we may monitor the flight arrival time to help coordinate airport pickup.",
      "Flight monitoring depends on publicly available flight information and does not replace the passenger's responsibility to provide accurate travel details.",
    ],
  },
  {
    title: "Waiting Time Policy",
    items: [
      "For airport pickups, reasonable waiting time is included after the confirmed landing or pickup time.",
      "Long delays, missed contact, or changes that require extended driver waiting may result in additional waiting charges.",
    ],
  },
  {
    title: "Cancellation Policy",
    items: [
      "Please cancel as early as possible if your travel plans change.",
      "Late cancellations, no-shows, or cancellations after a driver has already been dispatched may be subject to a cancellation charge.",
    ],
  },
  {
    title: "Passenger Responsibility",
    items: [
      "Passengers are responsible for providing accurate booking information and being ready at the agreed pickup point and time.",
      "Passengers must comply with local laws and behave respectfully toward the driver, vehicle, and other passengers.",
    ],
  },
  {
    title: "Luggage Responsibility",
    items: [
      "Passengers are responsible for their own luggage and personal belongings during the transfer.",
      "Please tell us in advance about oversized luggage, sports equipment, child seats, or special requirements so we can confirm suitable vehicle availability.",
    ],
  },
  {
    title: "Airport Pickup Details",
    items: [
      "Airport pickup instructions will be shared during booking confirmation and may include meeting point, terminal, or driver contact details.",
      "If you cannot find your driver or meeting point, contact Limassol Airport Taxi immediately by phone or WhatsApp.",
    ],
  },
];

function TermsPage() {
  return (
    <main className="min-h-screen bg-[#030712] px-6 py-16 text-white">
      <Helmet>
        <title>Terms &amp; Conditions | Limassol Airport Taxi</title>
        <meta
          name="description"
          content="Terms and Conditions for Limassol Airport Taxi bookings and airport transfer services in Cyprus."
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Terms & Conditions | Limassol Airport Taxi" />
        <meta
          property="og:description"
          content="Terms and Conditions for Limassol Airport Taxi bookings and airport transfer services in Cyprus."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://limassolairporttaxi.com/terms-and-conditions" />
        <link rel="canonical" href="https://limassolairporttaxi.com/terms-and-conditions" />
      </Helmet>

      <section className="mx-auto max-w-4xl">
        <Link
          to="/"
          className="inline-flex rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-sm font-bold text-white transition hover:border-yellow-400/50 hover:text-yellow-300"
        >
          Back to Home
        </Link>

        <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/30 sm:p-10">
          <p className="text-sm font-bold uppercase tracking-wide text-yellow-400">
            Limassol Airport Taxi
          </p>

          <h1 className="mt-3 text-4xl font-black text-yellow-400 sm:text-5xl">
            Terms &amp; Conditions
          </h1>

          <p className="mt-5 leading-8 text-neutral-300">
            These Terms &amp; Conditions explain the main booking, fare, pickup,
            waiting time, cancellation, and passenger responsibilities for
            Limassol Airport Taxi airport transfers and taxi services in Cyprus.
          </p>
        </div>

        <div className="mt-8 grid gap-5">
          {sections.map((section) => (
            <section
              key={section.title}
              className="rounded-3xl border border-white/10 bg-black/30 p-6 sm:p-8"
            >
              <h2 className="text-2xl font-bold text-yellow-400">
                {section.title}
              </h2>

              <ul className="mt-4 space-y-3 text-neutral-300">
                {section.items.map((item) => (
                  <li key={item} className="flex gap-3 leading-7">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-yellow-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <section className="mt-8 rounded-3xl border border-yellow-400/20 bg-yellow-400/10 p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-yellow-400">Contact</h2>

          <p className="mt-4 leading-8 text-neutral-200">
            For questions about these terms, fare confirmation, booking changes,
            cancellations, or airport pickup details, contact Limassol Airport
            Taxi by email, phone, or WhatsApp.
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <a
              href="mailto:info@limassolairporttaxi.com"
              className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-center font-bold text-white transition hover:border-yellow-400/50 hover:text-yellow-300"
            >
              Email Us
            </a>

            <a
              href="tel:+35796613191"
              onClick={() => trackPhoneClick("terms_phone")}
              className="rounded-2xl bg-yellow-400 px-5 py-3 text-center font-bold text-black transition hover:bg-yellow-300"
            >
              Call +357 96 613 191
            </a>

            <a
              href="https://wa.me/35796613191?text=Hello,%20I%20have%20a%20question%20about%20your%20terms."
              onClick={() => trackWhatsAppClick("terms_whatsapp")}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-center font-bold text-white transition hover:border-yellow-400/50 hover:text-yellow-300"
            >
              WhatsApp
            </a>
          </div>
        </section>
      </section>
    </main>
  );
}

export default TermsPage;
